import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { ShieldAlert, ShieldCheck, Send, CloudOff, Loader2 } from 'lucide-react'
import {
  ApiError,
  ModuleUnavailable,
  SessionExpired,
  onModuleUnavailable,
  onStepUpRequired,
  setRefreshHandler,
  stepup,
} from './api'
import { CasesSurface } from './Shell'

// ======================================================================
// THE STEP-UP GATE
// ======================================================================
//
// The piece with no precedent in this codebase, and the reason it comes
// first: every ER endpoint is behind it, so nothing else can be reached
// or tested until it works.
//
// ======================================================================
// FOUR STATUSES, FOUR SCREENS, NEVER COLLAPSED
// ======================================================================
//
//   401                     no Kwery session. The ordinary login
//                           redirect — and the ONLY one of the four that
//                           takes the user away from this URL. The
//                           intended path is stashed first; see
//                           rememberCasesDestination().
//
//   403 STEPUP_REQUIRED     the session is fine and the person is who
//                           they say; they have not proved it recently.
//                           The code prompt. NOT a sign-out: doing that
//                           to somebody who is correctly logged in,
//                           because they clicked Cases, would log them
//                           out of payroll and leave as well.
//
//   404                     the ACL's uniform refusal. Handled far below
//                           this component, per section, by rendering
//                           nothing. It never reaches here.
//
//   503                     the module cannot serve. ITS OWN SCREEN, AND
//                           THIS IS THE ONE THAT MATTERS MOST HERE.
//
// ======================================================================
// WHY 503 CANNOT BE ALLOWED TO LOOK LIKE 403
// ======================================================================
//
// During a Redis outage or a schema-gate failure the ER routes 503 —
// and so does the challenge endpoint, because requesting a code needs
// Redis to store it and email to send it. A gate that showed the code
// prompt on any refusal would then offer the user a remedy that cannot
// work: press "send me a code", get a 503, press it again. A keypad on
// a locked door, forever, with no text explaining that the lock is
// broken rather than the key.
//
// So ModuleUnavailable is its own error class in api.js, its own phase
// here, and its own screen with no code prompt on it. Every path into
// the challenge flow — the entry probe, requesting a code, verifying
// one — converts a 503 into that screen rather than staying on the
// prompt.
//
// The one thing that does NOT 503 is the entry probe itself:
// stepup_status answers `false` rather than 503 when Redis is down,
// deliberately, because it is a probe used to decide whether to draw a
// prompt. That is why the outage usually surfaces on the first real
// request instead, and why this component listens for it globally
// rather than only checking on mount.
//
// ======================================================================
// KEEPING THE USER'S PLACE
// ======================================================================
//
// The marker has an idle window and an absolute ceiling, so being
// re-prompted mid-session is expected behaviour, not an error — a case
// owner reading a long investigation report WILL meet this.
//
// Two things are therefore true of the re-prompt and neither is
// optional:
//
//   IT NEVER NAVIGATES. The prompt is drawn OVER the workspace, at the
//   same URL, and the children stay MOUNTED underneath. A half-typed
//   working note, an open modal, a selected file waiting to upload —
//   all still there when the six digits are accepted. Unmounting and
//   remounting would have been simpler and would have thrown all of it
//   away.
//
//   THE DATA REFETCHES WITHOUT THE FORMS RESETTING. A `generation`
//   counter goes up on each successful verification and is a dependency
//   of every useResource. Sections whose reads were refused while the
//   marker was dead reload; component state does not.

const StepUpContext = createContext({ generation: 0 })

/** Consumed by useResource so that everything the gate covers refetches
 *  when a new marker is obtained. */
export function useStepUpGeneration() {
  return useContext(StepUpContext).generation
}

/** Where to send somebody back to after they log in.
 *
 *  IT HAS TO BE STASHED RATHER THAN PASSED. ProtectedRoute redirects to
 *  /login with `replace` and no `from` state, and LoginPage hands off to
 *  utils/postLoginRedirect.js, which decides between /superadmin,
 *  /onboarding and /welcome and knows nothing about an intended
 *  destination. Neither file is this task's to edit. sessionStorage
 *  survives the hard `window.location.href` navigation LoginPage does,
 *  is per-tab, and is cleared the moment it is used. */
const RETURN_KEY = 'cases.returnTo'

export function rememberCasesDestination(pathname) {
  try {
    if (pathname && pathname.startsWith('/cases')) {
      sessionStorage.setItem(RETURN_KEY, pathname)
    }
  } catch {
    /* private mode — the deep link degrades to /cases, nothing breaks */
  }
}

export function takeCasesDestination() {
  try {
    const v = sessionStorage.getItem(RETURN_KEY)
    if (v) sessionStorage.removeItem(RETURN_KEY)
    return v
  } catch {
    return null
  }
}

const PHASE = {
  CHECKING: 'checking',
  PROMPT: 'prompt',
  CODE_SENT: 'code-sent',
  VERIFIED: 'verified',
  UNAVAILABLE: 'unavailable',
}

export default function StepUpGate({ children, onSignedOut, silentRefresh }) {
  const [phase, setPhase] = useState(PHASE.CHECKING)
  const [generation, setGeneration] = useState(0)
  const [everVerified, setEverVerified] = useState(false)
  const [expired, setExpired] = useState(false)
  const [unavailableMessage, setUnavailableMessage] = useState(null)

  const [sentTo, setSentTo] = useState(null)
  const [codeSeconds, setCodeSeconds] = useState(0)
  const [code, setCode] = useState('')
  // ====================================================================
  // THE DEMO CODE
  // ====================================================================
  //
  // Non-null ONLY when the backend put a `demo_code` on the challenge
  // response, which it does only for an account in the demo
  // organisation on a deployment with ER_DEMO_STEPUP_ENABLED set. See
  // the DEMO CODE block in er_cases/stepup.py.
  //
  // THE PROMPT IS NOT SKIPPED, AND THAT IS THE POINT. Step-up is a
  // feature of this module, not an obstacle in front of it, and a demo
  // that silently walked past it would be a demo of a security control
  // nobody saw. So the same screen appears, the same real endpoint is
  // called, the same real marker comes back — the code is simply
  // already in the box, with a line saying why.
  //
  // THE CODE IS NEVER HELD AS A CONSTANT HERE. It arrives from the
  // server or it does not exist; a copy in this file would be a second
  // definition of an authentication value that could disagree with the
  // first.
  const [demoCode, setDemoCode] = useState(null)
  const [busy, setBusy] = useState(null) // 'request' | 'verify' | null
  const [error, setError] = useState(null)

  const mounted = useRef(true)
  useEffect(() => () => { mounted.current = false }, [])

  // ====================================================================
  // THE CALLBACKS ARE HELD IN REFS, AND THIS IS NOT A MICRO-OPTIMISATION
  // ====================================================================
  //
  // context/AuthContext.jsx tracks activity with
  // `setLastActivity(Date.now())` on mousemove, keydown, click and
  // scroll, and builds a NEW context value object on every render. So
  // AuthProvider re-renders on essentially every mouse movement, every
  // consumer re-renders with it, and CasesRoute hands this component a
  // freshly-allocated onSignedOut each time.
  //
  // With those functions in a useCallback dependency array, `probe` was
  // rebuilt on every one of those renders, its useEffect refired, and
  // the first line of probe is setPhase(CHECKING). The observable result
  // was a gate stuck permanently on "Checking your access…", firing
  // /stepup/status several times a second, with the code prompt
  // appearing and being torn down before anybody could type into it.
  // Found by the first end-to-end run, not by reading.
  //
  // Refs keep the latest function without making its identity a
  // dependency of anything.
  const signedOutRef = useRef(onSignedOut)
  signedOutRef.current = onSignedOut
  const refreshRef = useRef(silentRefresh)
  refreshRef.current = silentRefresh

  // The XHR upload path is invisible to AuthContext's window.fetch
  // interceptor, so it needs a way to reach the same single-flight
  // refresh rather than starting a second one against a token the
  // backend blacklists on use.
  useEffect(() => {
    setRefreshHandler((...args) => refreshRef.current?.(...args))
    return () => setRefreshHandler(null)
  }, [])

  const goUnavailable = useCallback((e) => {
    if (!mounted.current) return
    setUnavailableMessage(e?.message || null)
    setPhase(PHASE.UNAVAILABLE)
  }, [])

  // ---- the entry probe ------------------------------------------------
  //
  // Called ON ENTRY so the prompt is shown before a request fails under
  // a half-drawn page. Without it the first thing the user sees is a
  // case workspace assembling itself and then being covered.
  const probe = useCallback(async () => {
    setPhase(PHASE.CHECKING)
    try {
      const res = await stepup.status()
      if (!mounted.current) return
      if (res?.verified) {
        setPhase(PHASE.VERIFIED)
        setEverVerified(true)
      } else {
        setPhase(PHASE.PROMPT)
      }
    } catch (e) {
      if (!mounted.current) return
      if (e instanceof SessionExpired) { signedOutRef.current?.(); return }
      if (e instanceof ModuleUnavailable) { goUnavailable(e); return }
      // A network failure or anything else unexpected. The prompt is
      // still the right screen — it is the one with a working button on
      // it, and the error is shown above it rather than swallowed.
      setError(e)
      setPhase(PHASE.PROMPT)
    }
  }, [goUnavailable])

  useEffect(() => { probe() }, [probe])

  // ---- global listeners -----------------------------------------------
  //
  // A marker can die between any two requests, and an outage can start
  // at any point. Both surface from wherever they happen, at whatever
  // depth, without a dozen components having to thread them upward.
  useEffect(() => {
    const off1 = onStepUpRequired(() => {
      if (!mounted.current) return
      setPhase((p) =>
        p === PHASE.UNAVAILABLE || p === PHASE.CODE_SENT ? p : PHASE.PROMPT,
      )
      setExpired(true)
    })
    const off2 = onModuleUnavailable((e) => goUnavailable(e))
    return () => { off1(); off2() }
  }, [goUnavailable])

  // ---- the code's own countdown ---------------------------------------
  useEffect(() => {
    if (phase !== PHASE.CODE_SENT || codeSeconds <= 0) return undefined
    const t = setInterval(() => setCodeSeconds((s) => Math.max(0, s - 1)), 1000)
    return () => clearInterval(t)
  }, [phase, codeSeconds])

  const requestCode = useCallback(async () => {
    setBusy('request')
    setError(null)
    try {
      const res = await stepup.request()
      if (!mounted.current) return
      setSentTo(res?.sent_to || null)
      setCodeSeconds(res?.expires_in || 0)
      // Pre-filled on the demo path, empty everywhere else. The input
      // stays editable either way — a demo where the code cannot be
      // changed is a demo where a wrong code cannot be shown, and the
      // refusal is part of what there is to show.
      setDemoCode(res?.demo_code || null)
      setCode(res?.demo_code || '')
      setPhase(PHASE.CODE_SENT)
    } catch (e) {
      if (!mounted.current) return
      if (e instanceof SessionExpired) { signedOutRef.current?.(); return }
      // THE LOOP-BREAKER. Without this the user presses the only button
      // on the screen and lands back on the same screen, indefinitely.
      if (e instanceof ModuleUnavailable) { goUnavailable(e); return }
      setError(e)
    } finally {
      if (mounted.current) setBusy(null)
    }
  }, [goUnavailable])

  const verify = useCallback(async () => {
    setBusy('verify')
    setError(null)
    try {
      const res = await stepup.verify(code)
      if (!mounted.current) return
      if (!res?.verified) {
        setError(new ApiError(400, 'That code was not accepted.'))
        return
      }
      setPhase(PHASE.VERIFIED)
      setEverVerified(true)
      setExpired(false)
      setCode('')
      setSentTo(null)
      setDemoCode(null)
      // Everything the gate covers reloads; nothing it covers unmounts.
      setGeneration((g) => g + 1)
    } catch (e) {
      if (!mounted.current) return
      if (e instanceof SessionExpired) { signedOutRef.current?.(); return }
      if (e instanceof ModuleUnavailable) { goUnavailable(e); return }
      // A wrong code, an exhausted challenge, a rate limit. The
      // backend's own wording is shown; see Refusal in ui.jsx for why it
      // is never rewritten.
      setError(e)
    } finally {
      if (mounted.current) setBusy(null)
    }
  }, [code, goUnavailable])

  if (phase === PHASE.UNAVAILABLE) {
    return <Unavailable message={unavailableMessage} onRetry={probe} />
  }

  const covering = phase === PHASE.PROMPT || phase === PHASE.CODE_SENT

  // THE CHILDREN STAY MOUNTED once the user has verified even once, so a
  // mid-session re-prompt keeps their place in the most literal sense:
  // the same components, holding the same state, under the same URL.
  const showChildren = phase === PHASE.VERIFIED || (everVerified && covering)

  return (
    <StepUpContext.Provider value={{ generation }}>
      {showChildren ? children : null}
      {phase === PHASE.CHECKING && !everVerified && <Checking />}
      {covering && (
        <Challenge
          overlay={everVerified}
          expired={expired}
          phase={phase}
          sentTo={sentTo}
          demoCode={demoCode}
          code={code}
          setCode={setCode}
          seconds={codeSeconds}
          busy={busy}
          error={error}
          onRequest={requestCode}
          onVerify={verify}
        />
      )}
    </StepUpContext.Provider>
  )
}

// ======================================================================
// SCREENS
// ======================================================================

function Checking() {
  return (
    <CasesSurface>
      <div className="mx-auto flex w-full max-w-md items-center gap-2.5 px-5 py-24 text-faint">
        <Loader2 size={16} className="animate-spin" strokeWidth={2} />
        <span className="text-[13px]">Checking your access…</span>
      </div>
    </CasesSurface>
  )
}

function mmss(total) {
  const s = Math.max(0, Math.floor(total))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** THE PROMPT.
 *
 *  Visually modelled on pages/superadmin/SuperadminSettings.jsx's MFA
 *  banner — amber while unverified, the same six-digit monospaced input,
 *  the same code countdown and resend — because a second visual
 *  convention for "confirm your identity" is how one of them ends up
 *  looking like a phishing screen to the person reading it.
 *
 *  It differs in one way that matters: that one is a BANNER over a form
 *  the user can still see. This is the whole screen, because there is
 *  nothing behind it to show — every ER endpoint is refused until it is
 *  answered. On a re-prompt it becomes an overlay instead, and the
 *  workspace IS still behind it. */
function Challenge({
  overlay, expired, phase, sentTo, demoCode, code, setCode, seconds, busy, error,
  onRequest, onVerify,
}) {
  const body = (
    <div className="panel anim-rise w-full max-w-md p-6">
      <div className="flex items-start gap-3">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] border border-amber-line bg-amber-dim">
          <ShieldAlert size={17} strokeWidth={1.8} className="text-amber" />
        </div>
        <div className="min-w-0">
          <h1 className="font-head text-[17px] font-semibold text-ink">
            {expired ? 'Confirm your identity again' : 'Confirm your identity'}
          </h1>
          <p className="mt-1 text-[12.5px] leading-relaxed text-muted">
            {expired
              ? 'Your verification has run out. Cases asks again after a period of inactivity, and after a fixed time whatever you are doing. Nothing you have open has been lost.'
              : 'Cases holds disciplinary and grievance records. Before it opens, confirm you are the account holder with a six-digit code sent to your email address.'}
          </p>
        </div>
      </div>

      {error && <ChallengeError error={error} />}

      {phase === PHASE.PROMPT ? (
        <button
          type="button"
          onClick={onRequest}
          disabled={busy === 'request'}
          className="btn btn-primary mt-5 w-full justify-center"
        >
          {busy === 'request' ? (
            <Loader2 size={14} className="animate-spin" strokeWidth={2} />
          ) : (
            <Send size={14} strokeWidth={1.9} />
          )}
          {busy === 'request' ? 'Sending…' : 'Email me a code'}
        </button>
      ) : (
        <form
          className="mt-5"
          onSubmit={(e) => {
            e.preventDefault()
            if (code.length === 6 && busy !== 'verify') onVerify()
          }}
        >
          {demoCode ? (
            /* THE DEMO LINE. It says three things, in this order,
               because a prospect reads the first sentence and skims the
               rest: this is a demo, a real deployment does not do this,
               and everything else you are about to see is the real
               control. Naming the address the code WOULD have gone to
               is what makes the second sentence concrete rather than a
               disclaimer. */
            <div className="rounded-md border border-accent-line bg-accent-dim px-3.5 py-2.5">
              <p className="font-head text-[12.5px] font-semibold text-accent">
                Demo — the code is already filled in
              </p>
              <p className="mt-1 text-[12px] leading-relaxed text-muted">
                In a real deployment Cases emails a six-digit code to the
                account holder — for this account,{' '}
                <span className="text-ink">{sentTo || 'their address'}</span> —
                and fills in nothing. Nothing else here is simulated: the same
                check runs, and the verification it grants lasts 30 minutes of
                inactivity and four hours at most, exactly as it would for a
                customer.
              </p>
            </div>
          ) : (
            <p className="text-[12.5px] text-muted">
              {/* The address is redacted BY THE BACKEND. Not for the
                  caller's benefit — they know their own email — but for
                  the screen, which in an HR office is read by whoever is
                  standing behind it. */}
              Enter the code sent to <span className="text-ink">{sentTo || 'your email'}</span>.
            </p>
          )}

          <input
            className="field mono mt-3 text-center"
            style={{ fontSize: 24, letterSpacing: 8, fontFamily: 'var(--font-mono)' }}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            placeholder="000000"
            autoFocus
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />

          <div className="mt-3 flex items-center justify-between gap-3">
            <button
              type="submit"
              disabled={code.length !== 6 || busy === 'verify'}
              className="btn btn-primary flex-1 justify-center"
            >
              {busy === 'verify' ? (
                <Loader2 size={14} className="animate-spin" strokeWidth={2} />
              ) : (
                <ShieldCheck size={14} strokeWidth={1.9} />
              )}
              {busy === 'verify' ? 'Checking…' : 'Confirm'}
            </button>
            {seconds > 0 && (
              <span className="mono shrink-0 text-amber">{mmss(seconds)}</span>
            )}
          </div>

          <button
            type="button"
            onClick={onRequest}
            disabled={busy === 'request'}
            className="btn btn-ghost btn-sm mt-2 w-full justify-center"
          >
            {busy === 'request' ? 'Sending…' : 'Send a new code'}
          </button>
        </form>
      )}
    </div>
  )

  if (overlay) {
    return (
      <div className="cases-root anim-fade fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm sm:p-10">
        <div className="my-auto w-full max-w-md">{body}</div>
      </div>
    )
  }
  return (
    <CasesSurface>
      <div className="flex min-h-screen items-start justify-center px-5 py-16 sm:py-24">
        {body}
      </div>
    </CasesSurface>
  )
}

/** The backend's own wording, never rewritten — except for the two
 *  statuses whose `detail` is not a sentence anybody wrote for a user. */
function ChallengeError({ error }) {
  const text =
    error?.status === 0
      ? 'Cases could not be reached. Check your connection and try again.'
      : error?.status === 429
        ? 'Too many attempts. Wait a minute before asking for another code.'
        : error?.message || 'That could not be completed.'
  return (
    <p
      role="alert"
      className="anim-fade mt-4 rounded-md border border-danger-line bg-danger-dim px-3.5 py-2.5 text-[12.5px] leading-relaxed text-ink"
    >
      {text}
    </p>
  )
}

/** THE 503 SCREEN. No code prompt on it, by design — see the block at
 *  the top of this file. Retry is a manual button rather than a poll:
 *  an outage that is being retried every two seconds by every open tab
 *  is an outage being made worse by its own users. */
function Unavailable({ message, onRetry }) {
  return (
    <CasesSurface>
      <div className="flex min-h-screen items-start justify-center px-5 py-16 sm:py-24">
        <div className="panel anim-rise w-full max-w-md p-6">
          <div className="flex items-start gap-3">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] border border-danger-line bg-danger-dim">
              <CloudOff size={17} strokeWidth={1.8} className="text-danger" />
            </div>
            <div className="min-w-0">
              <h1 className="font-head text-[17px] font-semibold text-ink">
                Cases is unavailable
              </h1>
              <p className="mt-1 text-[12.5px] leading-relaxed text-muted">
                {message || 'Cases is temporarily unavailable.'}
              </p>
              <p className="mt-2 text-[12.5px] leading-relaxed text-muted">
                This is not a problem with your account and there is nothing to
                confirm. The rest of Kwery is unaffected. If a deadline on a
                case falls today, tell whoever is handling it by another route.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onRetry}
            className="btn mt-5 w-full justify-center"
          >
            Try again
          </button>
        </div>
      </div>
    </CasesSurface>
  )
}
